import React, { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';

import { useGetCards } from '../services/auditService';
import { useGetAuditReviews } from '../services/reviewService';
import { titleToKeyMapping } from '../services/standardMappingTransformer';
import { TransformedAuditBoard } from '../Types/Audit';
import { Card } from '../Types/Card';
import { ReviewStatus, Review, reviewToApiStatusMap, ReviewType } from '../Types/Review';
import { StandardMapping } from '../Types/Standards';

interface ExportXLSXProps {
    selectedAudit?: TransformedAuditBoard;
    filename: string;
}

interface ColumnDefinition {
    headerName: string;
    field: string;
}

type CardWithReviews = Card & {
    supervisor?: { status: string, messages: any };
    manager?: { status: string, messages: any };
};

const keyToTitleMapping: Record<keyof StandardMapping, string> = Object.keys(
    titleToKeyMapping,
).reduce(
    (acc, title) => {
        const key = titleToKeyMapping[title as keyof typeof titleToKeyMapping];
        acc[key] = title;
        return acc;
    },
    {} as Record<keyof StandardMapping, string>,
);

const generateStandardsColumnDefinitions = (auditTypes: string[]): ColumnDefinition[] => {
    const keys = (Object.keys(keyToTitleMapping) as Array<keyof StandardMapping>).filter(
        (key) => auditTypes.includes(keyToTitleMapping[key]),
    );
    return keys.map((k) => {
        return { headerName: keyToTitleMapping[k as keyof StandardMapping], field: k };
    });
};

const generateStandardsRowData = (
    standards: ColumnDefinition[],
    values: StandardMapping,
): XLSX.CellObject[] => {
    return standards.map((standard) => {
        return {
            v: (values[standard.field as keyof StandardMapping] || []).flat().join(','),
            t: 's',
        };
    });
};
const ExportXLSX: React.FC<ExportXLSXProps> = ({ selectedAudit, filename }) => {
    const { data: cards, error, isLoading } = useGetCards(selectedAudit?.id);
    const {
        data: reviewData,
        error: _reviewError,
        isLoading: _reviewLoading,
    } = useGetAuditReviews(selectedAudit?.id);

    const [updatedCards, setUpdatedCards] = useState<CardWithReviews[]>([]);

    useEffect(() => {
        if (Array.isArray(cards)) {
            const updatedData = cards.map((card) => {
                const filteredReviews = reviewData?.filter(
                    (review: Review) => review && review.reviewableId === card.id,
                );

                const reviewsByType = filteredReviews?.reduce(
                    (acc: Partial<Record<ReviewType, Review>>, review) => {
                        if (review.reviewType && ReviewType[review.reviewType]) {
                            acc[review.reviewType] = review;
                        }
                        return acc;
                    },
                    {},
                );

                return { ...card, ...reviewsByType };
            });
            setUpdatedCards(updatedData as CardWithReviews[]);
        }
    }, [cards, reviewData]);

    if (error || isLoading) return '';
    const generateXLSX = () => {
        const workbook = XLSX.utils.book_new();
        const standards = generateStandardsColumnDefinitions(
            selectedAudit?.auditType || [],
        );
        // Generate headers dynamically
        const headers: XLSX.CellObject[] = [
            { v: 'Ref ID', t: 's' },
            { v: 'Control Name', t: 's' },
            { v: 'Section', t: 's' },
            { v: 'Control Description', t: 's' },
            { v: 'Service Auditor Tests', t: 's' },
            { v: 'Importance', t: 's' },
            { v: 'Frequency', t: 's' },
            { v: 'Board Status', t: 's' },
            { v: 'Testing Status', t: 's' },
            { v: 'Results', t: 's' },
            { v: 'Manager Status', t: 's' },
            { v: 'Manager Comments', t: 's' },
            { v: 'Supervisor Status', t: 's' },
            { v: 'Supervisor Comments', t: 's' },
            { v: 'Comments', t: 's' },
            ...standards.map((standard) => ({
                v: keyToTitleMapping[standard.field as keyof StandardMapping],
                t: 's' as XLSX.ExcelDataType,
            })),
        ];

        // Add headers to worksheet data
        const ws_data: XLSX.CellObject[][] = [headers];

        const formatMessages = (source: any | undefined) => {
            if (source?.messages && source.messages.length > 0) {
                const msgs = source.messages.map(
                     (m: any) =>
                        `${m.createdBy.firstName} ${m.createdBy.lastName}(${m.createdBy.email} - ${m.createdAt}): ${m.message}`,
                );
                return JSON.stringify(msgs);
            }
            return;
        };

        const formatStatus = (reviewStatus: string | undefined) => {
            const str = reviewToApiStatusMap[(reviewStatus as ReviewStatus)] || '';
            return str
                .split('_')
                .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
        };

        // Add row data
        updatedCards?.forEach((pCard: Partial<Card>) => {
            const card = pCard as CardWithReviews;
            const addRow = (row: XLSX.CellObject[]) => {
                ws_data.push(row);
            };
            const baseRow: XLSX.CellObject[] = [
                { v: card.refId, t: 's' },
                { v: card.title, t: 's' },
                { v: card.section, t: 's' },
                {
                    v: card.controlDescription,
                    t: 's',
                },
                {
                    v: card.auditProcedure,
                    t: 's',
                },
                { v: card.importance, t: 's' },
                { v: card.frequency, t: 's' },
                { v: card.status, t: 's' },
                { v: card.worksheetStatus, t: 's' },
                {
                    v: card.results?.status && card.results?.details?.tests
                        ? `${card.results.status}: ${card.results.details.tests}`
                        : card.results?.status || card.results?.details?.tests || '',
                    t: 's'
                },
                { v: formatStatus(card.manager?.status) || '', t: 's' },
                { v: formatMessages(card?.manager) || '', t: 's' },
                { v: formatStatus(card.supervisor?.status) || '', t: 's' },
                { v: formatMessages(card?.supervisor) || '', t: 's' },
                {
                    v: card.uwvComments,
                    t: 's',
                },
                ...generateStandardsRowData(standards, card.standardMapping),
            ];
            addRow(baseRow);
        });

        const worksheet = XLSX.utils.aoa_to_sheet(ws_data);
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Dash Export');
        XLSX.writeFile(workbook, `${filename}.xlsx`);
    };

    return (
        <button
            onClick={generateXLSX}
            className="rounded bg-indigo-600 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        >
            Export to XLSX
        </button>
    );
};

export default ExportXLSX;
